import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';

import {
  gerarListaEmailClientes,
  listarVendedoresEmail,
  listarRamosEmail,
  listarRegioesEmail,
  listarCidadesEmail,
} from '~/Services/Ferramentas/ListaEmailClientesService';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';

const ListaEmailClientes = () => {
  const [vendedor, setVendedor] = useState(null);
  const [ramo, setRamo] = useState(null);
  const [regiao, setRegiao] = useState(null);
  const [cidade, setCidade] = useState(null);

  const [opcoesVendedores, setOpcoesVendedores] = useState([]);
  const [opcoesRamos, setOpcoesRamos] = useState([]);
  const [opcoesRegioes, setOpcoesRegioes] = useState([]);
  const [opcoesCidades, setOpcoesCidades] = useState([]);

  const [emails, setEmails] = useState('');
  // 1:1 com Delphi UListaEmail.dfm Label1: Caption inicial '-', volta para '-' no Cancelar
  const [total, setTotal] = useState('-');
  const [loading, setLoading] = useState(false);
  // 1:1 com Delphi UListaEmail.pas:BitBtn2Click:150 — RxDBLookupCombo1.SetFocus apos Cancelar
  const vendedorRef = useRef(null);
  // 1:1 com Delphi UListaEmail.pas:RxDBLookupComboNKeyPress (linhas 155-181)
  // — Enter chama Wm_NextDlgCtl: avanca foco para o proximo controle na tab order
  // Tab order Delphi: Vendedor(0) -> Ramo(1) -> Regiao(2) -> Cidade(3) -> Panel1 (BitBtn1 OK como TabOrder=0 interno)
  const ramoRef = useRef(null);
  const regiaoRef = useRef(null);
  const cidadeRef = useRef(null);
  const okRef = useRef(null);

  // Estado de menu aberto por select — quando aberto, deixa o react-select aplicar a selecao
  // no Enter (paridade Delphi: RxDBLookupCombo selecionava antes do Wm_NextDlgCtl).
  const [menuAberto, setMenuAberto] = useState({
    vendedor: false, ramo: false, regiao: false, cidade: false,
  });
  const aoAbrirMenu = (campo) => () => setMenuAberto((s) => ({ ...s, [campo]: true }));
  const aoFecharMenu = (campo) => () => setMenuAberto((s) => ({ ...s, [campo]: false }));

  const focarProximoNoEnter = (campo, proximoRef) => (e) => {
    if (e.key !== 'Enter') return;
    // Menu aberto: react-select consome o Enter para selecionar a opcao destacada — nao mexer.
    if (menuAberto[campo]) return;
    e.preventDefault();
    if (proximoRef.current) proximoRef.current.focus();
  };

  useEffect(() => {
    let ativo = true;

    // 1:1 com Delphi UListaEmail.pas:FormShow:89-103 — abre 4 datasets INDEPENDENTES.
    // Cada lookup popula seu proprio select assim que responde: um endpoint lento/travado
    // (ex.: vendedores) NAO pode bloquear ramo/regiao/cidade. Promise.allSettled acoplava
    // os 4 (so resolvia quando o ultimo settlava) — se vendedores ficasse pending, todos
    // os selects ficavam vazios. LookupDisplay do Delphi inclui Codigo + Descricao.
    const carregarLookup = (fetch, setOpcoes, mapItem, nome) =>
      fetch()
        .then((res) => {
          if (!ativo) return;
          const lista = Array.isArray(res) ? res : [];
          setOpcoes(lista.map(mapItem));
        })
        .catch(() => {
          if (!ativo) return;
          toast.error(`Erro ao carregar opções de filtro: ${nome}.`);
        });

    carregarLookup(
      listarVendedoresEmail,
      setOpcoesVendedores,
      (v) => ({ value: v.codigo_conta, label: `${v.codigo_conta} - ${v.apelido_conta}` }),
      'Vendedores'
    );
    carregarLookup(
      listarRamosEmail,
      setOpcoesRamos,
      (r) => ({ value: r.codigo_ramo, label: `${r.codigo_ramo} - ${r.descricao_ramo}` }),
      'Ramos'
    );
    carregarLookup(
      listarRegioesEmail,
      setOpcoesRegioes,
      (r) => ({ value: r.codigo_regiao, label: `${r.codigo_regiao} - ${r.descricao_regiao}` }),
      'Regiões'
    );
    carregarLookup(
      listarCidadesEmail,
      setOpcoesCidades,
      (c) => ({ value: c.codigo_cidade, label: c.descricao_cidade }),
      'Cidades'
    );

    return () => {
      ativo = false;
    };
  }, []);

  const handleGerar = async () => {
    setLoading(true);
    try {
      const result = await gerarListaEmailClientes({
        codigoVendedor: vendedor ? vendedor.value : null,
        codigoRamo: ramo ? ramo.value : null,
        codigoRegiao: regiao ? regiao.value : null,
        codigoCidade: cidade ? cidade.value : null,
      });
      const lista = result && Array.isArray(result.emails) ? result.emails : [];
      setEmails(lista.join('; '));
      setTotal(result && result.total != null ? result.total : lista.length);
    } catch (err) {
      toast.error(
        err && err.message ? err.message : 'Erro ao gerar lista de e-mails.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLimpar = () => {
    setVendedor(null);
    setRamo(null);
    setRegiao(null);
    setCidade(null);
    setEmails('');
    setTotal('-');
    if (vendedorRef.current) vendedorRef.current.focus();
  };

  // BUG-4 (W-0657): cascade Região→Cidade (melhoria; o Delphi não fazia). Ao trocar a região,
  // recarrega só as cidades dela e limpa a cidade selecionada.
  const handleRegiaoChange = (novaRegiao) => {
    setRegiao(novaRegiao);
    setCidade(null);
    listarCidadesEmail(novaRegiao ? novaRegiao.value : null)
      .then((res) => {
        const lista = Array.isArray(res) ? res : [];
        setOpcoesCidades(
          lista.map((c) => ({ value: c.codigo_cidade, label: c.descricao_cidade }))
        );
      })
      .catch(() => toast.error('Erro ao carregar opções de filtro: Cidades.'));
  };

  return (
    <div className="container-fluid mt-3">
      {/* 1:1 com Delphi UListaEmail.dfm:4 — Caption 'Lista de E-mail de cliente' (singular) */}
      <h4 className="mb-3">Lista de E-mail de cliente</h4>

      <Card className="mb-3">
        <Card.Body>
          <Form>
            {/* Filtros em linha (labels em cima) — padrao extranet (ref. ConsultaCgcCpf/FilterSection).
                Tab order preservada: Vendedor -> Ramo -> Regiao -> Cidade -> OK (Delphi). */}
            <Row>
              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">Vendedor</Form.Label>
                  <Select
                    ref={vendedorRef}
                    placeholder="Todos"
                    options={opcoesVendedores}
                    value={vendedor}
                    onChange={setVendedor}
                    isClearable
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                    onMenuOpen={aoAbrirMenu('vendedor')}
                    onMenuClose={aoFecharMenu('vendedor')}
                    onKeyDown={focarProximoNoEnter('vendedor', ramoRef)}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">Ramo de Atividade</Form.Label>
                  <Select
                    ref={ramoRef}
                    placeholder="Todos"
                    options={opcoesRamos}
                    value={ramo}
                    onChange={setRamo}
                    isClearable
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                    onMenuOpen={aoAbrirMenu('ramo')}
                    onMenuClose={aoFecharMenu('ramo')}
                    onKeyDown={focarProximoNoEnter('ramo', regiaoRef)}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">Região</Form.Label>
                  <Select
                    ref={regiaoRef}
                    placeholder="Todas"
                    options={opcoesRegioes}
                    value={regiao}
                    onChange={handleRegiaoChange}
                    isClearable
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                    onMenuOpen={aoAbrirMenu('regiao')}
                    onMenuClose={aoFecharMenu('regiao')}
                    onKeyDown={focarProximoNoEnter('regiao', cidadeRef)}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">Cidade</Form.Label>
                  <Select
                    ref={cidadeRef}
                    placeholder="Todas"
                    options={opcoesCidades}
                    value={cidade}
                    onChange={setCidade}
                    isClearable
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                    onMenuOpen={aoAbrirMenu('cidade')}
                    onMenuClose={aoFecharMenu('cidade')}
                    onKeyDown={focarProximoNoEnter('cidade', okRef)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex">
              <Button
                ref={okRef}
                variant="primary"
                onClick={handleGerar}
                disabled={loading}
                className="mr-2"
              >
                OK
              </Button>
              <Button variant="secondary" onClick={handleLimpar}>
                Cancelar
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {/* 1:1 com Delphi UListaEmail.dfm Label1 + UListaEmail.pas:140 — apenas o numero, sempre visivel */}
          <div className="mb-2">
            <strong>{total}</strong>
          </div>

          <Form.Control
            as="textarea"
            rows={8}
            readOnly
            value={emails}
            placeholder="Clique em OK para gerar a lista..."
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default ListaEmailClientes;
